import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AuthChangeEvent } from '@supabase/supabase-js';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase/client';
import { updatePassword } from '@/lib/api/auth';

const resetSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match.',
    path: ['confirm'],
  });
type ResetValues = z.infer<typeof resetSchema>;

export function VerifyEmail() {
  const navigate = useNavigate();
  const [authEvent, setAuthEvent] = useState<AuthChangeEvent | null>(null);
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ resolver: zodResolver(resetSchema) });

  useEffect(() => {
    // detectSessionInUrl:true means the client has already processed the token
    // in the URL hash by the time this runs. Subscribe to catch the resulting
    // auth state change.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      setAuthEvent(event);
      if (event === 'SIGNED_IN') {
        // AuthInit will create/sync the profile; send user to /me.
        navigate('/me', { replace: true });
      }
    });
    // Also check whether there's already a live session (token processed before
    // React mounted).
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session && authEvent !== 'PASSWORD_RECOVERY') {
        navigate('/me', { replace: true });
      }
    });
    return () => sub.subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = async (values: ResetValues) => {
    setResetError(null);
    try {
      await updatePassword(values.password);
      setResetDone(true);
      setTimeout(() => navigate('/', { replace: true }), 2000);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Could not update password.');
    }
  };

  // Password recovery flow — show set-new-password form.
  if (authEvent === 'PASSWORD_RECOVERY') {
    return (
      <PageShell>
        <Card>
          {resetDone ? (
            <>
              <CardHeader>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-success/15 text-success">
                  <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <CardTitle className="mt-4">Password updated</CardTitle>
                <CardDescription>Signing you in…</CardDescription>
              </CardHeader>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Set a new password</CardTitle>
                <CardDescription>Choose a strong password for your account.</CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleSubmit(onReset)}
                  noValidate
                  className="flex flex-col gap-4"
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="password">New password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      aria-invalid={!!errors.password}
                      {...register('password')}
                    />
                    {errors.password && (
                      <p role="alert" aria-live="polite" className="text-xs text-destructive">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      autoComplete="new-password"
                      aria-invalid={!!errors.confirm}
                      {...register('confirm')}
                    />
                    {errors.confirm && (
                      <p role="alert" aria-live="polite" className="text-xs text-destructive">
                        {errors.confirm.message}
                      </p>
                    )}
                  </div>
                  {resetError && (
                    <p role="alert" aria-live="polite"
                      className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      {resetError}
                    </p>
                  )}
                  <Button type="submit" variant="gold" disabled={isSubmitting}>
                    {isSubmitting ? 'Updating…' : 'Update password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </PageShell>
    );
  }

  // Waiting for token exchange / redirect.
  return (
    <PageShell>
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-64" />
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            Verifying your email… if this takes more than a few seconds,{' '}
            <Link to="/signin" className="font-medium text-gold-dark hover:underline">
              sign in manually
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-3 text-navy"
        >
          <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-10 w-10" />
          <span className="font-display text-base font-semibold">
            The Cambodian Professionals Network
          </span>
        </Link>
        {children}
      </div>
    </div>
  );
}
